import { useRef } from 'react';

interface PaneSplitterProps {
  /** Called continuously while dragging, with the horizontal delta (px) since the last event. */
  onDrag: (deltaX: number) => void;
  /** Called once when the drag ends (pointer released) — good time to persist. */
  onDragEnd: () => void;
  /** Called for keyboard-driven resize (arrow keys), in px. */
  onKeyboardResize: (deltaX: number) => void;
  ariaLabel: string;
  ariaValueNow: number;
  ariaValueMin: number;
  ariaValueMax: number;
}

const KEYBOARD_STEP_PX = 24;

/**
 * Thin draggable divider between two panes. Pointer events only (no
 * dependency), with a wider invisible hit-area than the visible bar, and
 * basic keyboard support (arrow keys) since this is a real interactive
 * control, not decoration.
 */
export function PaneSplitter({
  onDrag,
  onDragEnd,
  onKeyboardResize,
  ariaLabel,
  ariaValueNow,
  ariaValueMin,
  ariaValueMax,
}: PaneSplitterProps) {
  const lastXRef = useRef(0);

  function handlePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    e.currentTarget.setPointerCapture(e.pointerId);
    lastXRef.current = e.clientX;
  }

  function handlePointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
    const deltaX = e.clientX - lastXRef.current;
    lastXRef.current = e.clientX;
    if (deltaX !== 0) onDrag(deltaX);
  }

  function handlePointerUp(e: React.PointerEvent<HTMLDivElement>) {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    onDragEnd();
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'ArrowLeft') {
      onKeyboardResize(-KEYBOARD_STEP_PX);
      e.preventDefault();
    } else if (e.key === 'ArrowRight') {
      onKeyboardResize(KEYBOARD_STEP_PX);
      e.preventDefault();
    }
  }

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={ariaLabel}
      aria-valuenow={Math.round(ariaValueNow)}
      aria-valuemin={Math.round(ariaValueMin)}
      aria-valuemax={Math.round(ariaValueMax)}
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onKeyDown={handleKeyDown}
      className="relative w-1.5 shrink-0 cursor-col-resize select-none bg-gray-200 hover:bg-primary-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 transition-colors"
    >
      {/* Wider invisible hit-area so the 6px visual bar is easier to grab. */}
      <span className="absolute inset-y-0 -left-1.5 -right-1.5" aria-hidden="true" />
    </div>
  );
}
